import { industrySectors } from '../../../mocks/industries';

const COMPANY_ADDRESS = {
  street: 'C. Nicolás Ureña de Mendoza 55, Los Prados',
  city: 'Santo Domingo',
  region: 'Distrito Nacional',
  country: 'DO',
  postalCode: '10131',
  phone: '+18495388899',
  email: 'innoflextechnology@innoflexapp.com',
  url: 'https://innoflextechnology.com',
};

export default function IndustriesSEO() {
  const baseOrg = {
    '@type': 'Organization',
    name: 'InnoFlex',
    url: COMPANY_ADDRESS.url,
    telephone: COMPANY_ADDRESS.phone,
    email: COMPANY_ADDRESS.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: COMPANY_ADDRESS.street,
      addressLocality: COMPANY_ADDRESS.city,
      addressRegion: COMPANY_ADDRESS.region,
      postalCode: COMPANY_ADDRESS.postalCode,
      addressCountry: COMPANY_ADDRESS.country,
    },
  };

  const sectorSchemas = industrySectors.map((sector, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    item: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${COMPANY_ADDRESS.url}/industrias#${sector.id}`,
      name: sector.seoTitle,
      description: sector.seoDescription,
      url: `${COMPANY_ADDRESS.url}/industrias#${sector.id}`,
      keywords: sector.seoKeywords.join(', '),
      provider: baseOrg,
      areaServed: {
        '@type': 'Country',
        name: 'República Dominicana',
        identifier: 'DO',
      },
      audience: {
        '@type': 'BusinessAudience',
        audienceType: `${sector.title} en República Dominicana`,
      },
      category: sector.schemaType,
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: `Software de Gestión para ${sector.title}`,
        itemListElement: sector.features.slice(0, 3).map((feat, fi) => ({
          '@type': 'Offer',
          position: fi + 1,
          name: feat.title,
          description: feat.desc,
        })),
      },
    },
  }));

  const softwareApp = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'InnoFlex — Software de Gestión y Facturación e-CF',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, Android, iOS',
    url: COMPANY_ADDRESS.url,
    description: 'Sistema de gestión empresarial (ERP, POS, Contabilidad) con facturación electrónica e-CF certificada DGII para negocios en República Dominicana. Disponible para restaurantes, supermercados, ferreterías, minimarkets, dealers y agencias de préstamos.',
    keywords: industrySectors.flatMap((s) => s.seoKeywords).join(', '),
    author: baseOrg,
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: '29.99',
      highPrice: '149.99',
      offerCount: 4,
    },
    featureList: industrySectors.flatMap((s) => s.features.map((f) => f.title)),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '47',
      bestRating: '5',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: industrySectors.flatMap((sector) =>
      sector.faq.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      }))
    ),
  };

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Industrias y sectores atendidos por InnoFlex en República Dominicana',
    description: 'Software de gestión y facturación e-CF para restaurantes, supermercados, ferreterías, minimarkets, dealers y agencias de préstamos en República Dominicana.',
    numberOfItems: industrySectors.length,
    itemListElement: sectorSchemas,
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${COMPANY_ADDRESS.url}/industrias`,
    url: `${COMPANY_ADDRESS.url}/industrias`,
    name: 'Software de Facturación e-CF por Industria | InnoFlex República Dominicana',
    description: 'Conoce el software de gestión y facturación electrónica e-CF certificado DGII adaptado para cada sector en República Dominicana: restaurantes, supermercados, ferreterías y más.',
    inLanguage: 'es-DO',
    isPartOf: { '@type': 'WebSite', url: COMPANY_ADDRESS.url, name: 'InnoFlex' },
    dateModified: '2026-03-27',
    keywords: 'software facturación restaurante RD, sistema POS supermercado Santo Domingo, software ferretería República Dominicana, facturación e-CF DGII, ERP dealer RD',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Inicio', item: COMPANY_ADDRESS.url },
        { '@type': 'ListItem', position: 2, name: 'Industrias', item: `${COMPANY_ADDRESS.url}/industrias` },
      ],
    },
  };

  return (
    <>
      <title>Software de Facturación e-CF por Industria en RD | InnoFlex</title>
      <meta
        name="description"
        content="Software de gestión y facturación electrónica e-CF certificado DGII para restaurantes, supermercados, ferreterías y más en República Dominicana. Sistema POS, ERP y contabilidad."
      />
      <meta
        name="keywords"
        content="software facturación restaurante RD, sistema POS supermercado Santo Domingo, software ferretería República Dominicana, facturación e-CF DGII, ERP dealer RD, sistema colmado República Dominicana, software agencia préstamos RD"
      />
      <link rel="canonical" href={`${COMPANY_ADDRESS.url}/industrias`} />
      <meta name="last-modified" content="2026-03-27" />
      <meta property="og:title" content="Software de Facturación e-CF por Industria | InnoFlex RD" />
      <meta
        property="og:description"
        content="Sistema de gestión ERP, POS y facturación electrónica e-CF certificado DGII para cada sector en República Dominicana."
      />
      <meta property="og:url" content={`${COMPANY_ADDRESS.url}/industrias`} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(softwareApp)}</script>
      <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </>
  );
}
